import React from "react";

import { CREATIVE_TYPE_UI } from "../../services/creativeTypeOptions";

/**
 * The video choice made on screen 1, SHOWN — not asked again.
 *
 * FOUNDER RULING 2026-09-09: "The video format and video-length choice should
 * only exist on the very first screen, not repeated on every later step... every
 * later step should just use that stored choice rather than asking again."
 *
 * This is what replaces the dropdowns that were removed from step 4 (platform,
 * length) and step 6 (creative type). It renders no control, so there is nothing
 * on a later step that can disagree with step 1.
 *
 * WHEN A CHOICE WAS NEVER MADE it says so and offers the one button that leads
 * to where it is made. It does NOT offer a dropdown as a shortcut — a second
 * place to answer is the thing being removed. Live at the time of writing: only
 * **4 of 120 sessions carry a creative type and none carry a length**, so the
 * unmade case is the common one and is a real state, not an edge case.
 */
export function typeLabel(value) {
  if (!value) return null;
  const hit = CREATIVE_TYPE_UI.find((o) => o.value === value);
  if (hit) return hit.label;
  // A BARE TYPE IS A REAL STORED VALUE, and this is exactly what made the
  // Founder say "nothing has changed": 90 of 288 scripts carry `creative_type:
  // "AI"` with no destination, which matches none of the four option values. A
  // <select> given an unmatched value silently shows its placeholder, so a
  // chosen type rendered as "Choose type…". Named here instead of dropped.
  if (value === "AI") return "AI VIDEO (destination not recorded)";
  return value;
}

export default function SessionChoiceLine({
  sessionMeta, onGoToStep, firstStep = 0, testidPrefix = "session-choice",
}) {
  const format = sessionMeta?.video_format || null;
  const type = sessionMeta?.creative_type || null;
  const length = sessionMeta?.video_length || null;
  const missing = [
    !type ? "creative type" : null,
    !format ? "video format" : null,
    !length ? "video length" : null,
  ].filter(Boolean);

  return (
    <div className="ws-session-choice" data-testid={testidPrefix}>
      <p className="dim">
        <strong>Chosen for this session:</strong>{" "}
        <span data-testid={`${testidPrefix}-type`}>
          {typeLabel(type) || "no creative type"}
        </span>
        {" · "}
        <span data-testid={`${testidPrefix}-format`}>
          {format || "no video format"}
        </span>
        {" · "}
        <span data-testid={`${testidPrefix}-length`}>
          {length ? `${length} seconds` : "no length"}
        </span>
      </p>

      {missing.length ? (
        <p className="ws-step-note state-warn" data-testid={`${testidPrefix}-missing`}>
          This session has no {missing.join(", no ")}. It is chosen once, on
          step 1 — this step does not ask.
          {onGoToStep ? (
            <>
              {" "}
              <button type="button" className="btn-ghost btn-sm"
                      onClick={() => onGoToStep(firstStep)}
                      data-testid={`${testidPrefix}-goto`}>
                Choose it on step 1
              </button>
            </>
          ) : null}
        </p>
      ) : null}
    </div>
  );
}
